import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'
import {
  computeFitScale,
  FIT_SCALE_MAX,
  FIT_SCALE_MIN,
  shouldAcceptFitScaleResize,
} from './fitScale'

interface UseFitScaleOptions {
  /** Outer viewport that must fully contain the scaled content. */
  viewportRef: RefObject<HTMLElement | null>
  /** Inner content measured at scale 1. */
  contentRef: RefObject<HTMLElement | null>
  minScale?: number
  maxScale?: number
  /** Re-measure when this changes (e.g. detail open vs chart). */
  modeKey?: string | number | boolean
}

/**
 * Scale content down (CSS zoom) so it fits the viewport without clipping.
 * Never scales above 1. Floors at minScale so text stays usable.
 */
export function useFitScale({
  viewportRef,
  contentRef,
  minScale = FIT_SCALE_MIN,
  maxScale = FIT_SCALE_MAX,
  modeKey,
}: UseFitScaleOptions): { scale: number; style: CSSProperties } {
  const [scale, setScale] = useState(1)
  const lastBoxRef = useRef({ w: 0, h: 0 })

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const content = contentRef.current
    if (!viewport || !content) return

    function measure(force: boolean) {
      const vp = viewportRef.current
      const el = contentRef.current
      if (!vp || !el) return

      const box = {
        w: Math.round(vp.clientWidth),
        h: Math.round(vp.clientHeight),
      }
      if (box.w < 32 || box.h < 32) return
      if (!force && !shouldAcceptFitScaleResize({ prev: lastBoxRef.current, next: box })) {
        return
      }
      lastBoxRef.current = box

      // Measure intrinsic size at zoom 1 (ignore flex stretch / internal scroll).
      const prev = {
        zoom: el.style.zoom,
        height: el.style.height,
        minHeight: el.style.minHeight,
        flex: el.style.flex,
        overflow: el.style.overflow,
      }
      el.style.zoom = '1'
      el.style.height = 'max-content'
      el.style.minHeight = '0'
      el.style.flex = '0 0 auto'
      el.style.overflow = 'visible'
      const contentWidth = Math.max(el.scrollWidth, el.offsetWidth, 1)
      const contentHeight = Math.max(el.scrollHeight, el.offsetHeight, 1)
      el.style.zoom = prev.zoom
      el.style.height = prev.height
      el.style.minHeight = prev.minHeight
      el.style.flex = prev.flex
      el.style.overflow = prev.overflow

      const next = computeFitScale({
        containerWidth: Math.max(box.w - 4, 1),
        containerHeight: Math.max(box.h - 4, 1),
        contentWidth,
        contentHeight,
        minScale,
        maxScale,
      })
      setScale((current) => (Math.abs(current - next) < 0.01 ? current : next))
    }

    measure(true)
    const t = window.setTimeout(() => measure(true), 60)

    const ro = new ResizeObserver(() => measure(false))
    ro.observe(viewport)
    ro.observe(content)

    return () => {
      ro.disconnect()
      window.clearTimeout(t)
    }
  }, [viewportRef, contentRef, minScale, maxScale, modeKey])

  return {
    scale,
    style: {
      zoom: scale,
      // Firefox / older engines: transform fallback when zoom unsupported.
      // Chromium applies zoom; transform is harmless if zoom already shrank layout.
      ...(scale < 1
        ? ({
            ['--fit-scale' as string]: String(scale),
          } as CSSProperties)
        : null),
    },
  }
}
